import React, { useState, useEffect } from 'react';
import './InstructorPayout.css';
import useBlockCheck from '../../hooks/useBlockCheck';
import axios from 'axios';
import api from '../../api';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import InstaTopbar from '../InstaTopbar/InstaTopbar';

const InstructorPayout = () => {
  const { token, profile } = useAuth();
  useBlockCheck('instructor');

  const [payoutMethod, setPayoutMethod] = useState('Bank Transfer');
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [bankDetails, setBankDetails] = useState({
    accountHolder: '',
    accountNumber: '',
    ifsc: '',
    bankName: '',
  });

  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [earnings, setEarnings] = useState({
    thisMonth: 0,
    total: 0,
  });
  const [filteredEarnings, setFilteredEarnings] = useState({
    thisMonth: 0,
    total: 0,
  });
  const [loading, setLoading] = useState(true);

  // Filter states
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [showFilters, setShowFilters] = useState(false);
  const [filterType, setFilterType] = useState('all'); // 'all', 'year', 'month'

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [transactionsPerPage] = useState(10);

  // Generate years array (current year - 5 years)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 6 }, (_, i) => currentYear - i);

  // Months array
  const months = [
    { value: 0, name: 'January' },
    { value: 1, name: 'February' },
    { value: 2, name: 'March' },
    { value: 3, name: 'April' },
    { value: 4, name: 'May' },
    { value: 5, name: 'June' },
    { value: 6, name: 'July' },
    { value: 7, name: 'August' },
    { value: 8, name: 'September' },
    { value: 9, name: 'October' },
    { value: 10, name: 'November' },
    { value: 11, name: 'December' },
  ];

  // Fetch instructor's courses and calculate earnings
  useEffect(() => {
    const fetchInstructorData = async () => {
      try {
        setLoading(true);

        // Fetch published courses
        const { data: coursesData } = await axios.get(`${api.getCourses}?instructor=true`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (coursesData.courses) {
          // Calculate earnings from course sales
          const currentDate = new Date();
          const currentMonth = currentDate.getMonth();
          const currentYear = currentDate.getFullYear();

          let thisMonthEarnings = 0;
          let totalEarnings = 0;
          const transactionList = [];

          coursesData.courses.forEach(course => {
            if (course.enrolledStudents && course.enrolledStudents.length > 0) {
              course.enrolledStudents.forEach(student => {
                // Calculate earnings based on course price
                const coursePrice = course.isFree ? 0 : (course.offerPrice || course.actualPrice || 0);
                const fullEarnings = coursePrice;

                totalEarnings += fullEarnings;

                // Check if student has purchase date
                if (student.purchasedDate) {
                  const purchaseDate = new Date(student.purchasedDate);
                  const purchaseMonth = purchaseDate.getMonth();
                  const purchaseYear = purchaseDate.getFullYear();

                  if (purchaseMonth === currentMonth && purchaseYear === currentYear) {
                    thisMonthEarnings += fullEarnings;
                  }

                  // Add to transaction list
                  transactionList.push({
                    id: `${course._id}-${student._id || student.email}`,
                    date: student.purchasedDate,
                    amount: fullEarnings,
                    type: 'Course Sale',
                    status: 'Completed',
                    courseTitle: course.title,
                    studentName: student.name || student.email || 'Unknown Student',
                    purchaseYear: purchaseYear,
                    purchaseMonth: purchaseMonth,
                  });
                }
              });
            }
          });

          setEarnings({
            thisMonth: thisMonthEarnings,
            total: totalEarnings,
          });

          // Sort transactions by date (newest first)
          const sortedTransactions = transactionList.sort((a, b) =>
            new Date(b.date) - new Date(a.date),
          );

          setTransactions(sortedTransactions);
          setFilteredTransactions(sortedTransactions);
          setFilteredEarnings({
            thisMonth: thisMonthEarnings,
            total: totalEarnings,
          });
        }
      } catch (err) {
        console.error('Error fetching instructor data:', err);
      } finally {
        setLoading(false);
      }
    };

    if (token && profile) {
      fetchInstructorData();
    }
  }, [token, profile]);

  // Apply filters when filter criteria change
  useEffect(() => {
    applyFilters();
  }, [selectedYear, selectedMonth, filterType, transactions]);

  const applyFilters = () => {
    let filtered = [...transactions];
    let filteredEarningsData = { thisMonth: 0, total: 0 };

    if (filterType === 'year') {
      filtered = transactions.filter(txn => {
        const txnYear = new Date(txn.date).getFullYear();
        return txnYear === selectedYear;
      });
    } else if (filterType === 'month') {
      filtered = transactions.filter(txn => {
        const txnDate = new Date(txn.date);
        const txnYear = txnDate.getFullYear();
        const txnMonth = txnDate.getMonth();
        return txnYear === selectedYear && txnMonth === selectedMonth;
      });
    }

    // Calculate filtered earnings
    filtered.forEach(txn => {
      const txnDate = new Date(txn.date);
      const currentDate = new Date();
      const currentMonth = currentDate.getMonth();
      const currentYear = currentDate.getFullYear();

      filteredEarningsData.total += txn.amount;

      if (txnDate.getMonth() === currentMonth && txnDate.getFullYear() === currentYear) {
        filteredEarningsData.thisMonth += txn.amount;
      }
    });

    setFilteredTransactions(filtered);
    setFilteredEarnings(filteredEarningsData);
    setCurrentPage(1); // Reset to first page when filters change
  };

  // Pagination logic
  const indexOfLastTransaction = currentPage * transactionsPerPage;
  const indexOfFirstTransaction = indexOfLastTransaction - transactionsPerPage;
  const currentTransactions = filteredTransactions.slice(indexOfFirstTransaction, indexOfLastTransaction);
  const totalPages = Math.ceil(filteredTransactions.length / transactionsPerPage);

  // Change page
  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Go to previous page
  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Go to next page
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Generate page numbers array
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total pages is less than or equal to max visible
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      // Show limited pages with ellipsis
      if (currentPage <= 3) {
        // Show first 3 pages + ellipsis + last page
        for (let i = 1; i <= 3; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        // Show first page + ellipsis + last 3 pages
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = totalPages - 2; i <= totalPages; i++) {
          pageNumbers.push(i);
        }
      } else {
        // Show first page + ellipsis + current page + ellipsis + last page
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      }
    }

    return pageNumbers;
  };

  const handleFilterChange = (type) => {
    setFilterType(type);
    if (type === 'all') {
      setFilteredTransactions(transactions);
      setFilteredEarnings(earnings);
    }
  };

  const handleYearChange = (year) => {
    setSelectedYear(year);
  };

  const handleMonthChange = (month) => {
    setSelectedMonth(month);
  };

  const resetFilters = () => {
    setFilterType('all');
    setSelectedYear(new Date().getFullYear());
    setSelectedMonth(new Date().getMonth());
    setFilteredTransactions(transactions);
    setFilteredEarnings(earnings);
  };
  const navigate = useNavigate();
  const handleChange = (e) => {
    setBankDetails({ ...bankDetails, [e.target.name]: e.target.value });
  };

  const handleSave = () => {
    setShowPayoutModal(false);
    alert('Bank details updated successfully.');
  };

  const handleCloseModal = () => {
    setShowPayoutModal(false);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const formatCurrency = (amount) => {
    return `₹${amount.toFixed(2)}`;
  };

  const getFilterSummary = () => {
    if (filterType === 'all') return 'All Time';
    if (filterType === 'year') return `Year ${selectedYear}`;
    if (filterType === 'month') return `${months[selectedMonth].name} ${selectedYear}`;
    return 'All Time';
  };

  if (loading) {
    return (
      <div className="payout-container">
        <div className="payout-header">
          <h2>Payout Dashboard</h2>
          <button className="close-btn-po" onClick={() => navigate('/dashboard')}>
            ×
          </button>
        </div>
        <div style={{ textAlign: 'center', padding: '20px' }}>
          Loading...
        </div>
      </div>
    );
  }

  return (
    <div>
      <InstaTopbar />
      <div className="payout-container">

        <div className="payout-header">
          <h2>Payout Dashboard</h2>
          <button className="close-btn-po-s" onClick={() => navigate('/dashboard')}>
          ×
          </button>
        </div>

        {/* Filter Section */}
        <div className="filter-section">
          <div className="filter-header">
            <h3>Filter Transactions</h3>
            <button
              className="filter-toggle-btn"
              onClick={() => setShowFilters(!showFilters)}
            >
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </button>
          </div>

          {showFilters && (
            <div className="filter-controls">
              <div className="filter-buttons">
                <button
                  className={`filter-btn ${filterType === 'all' ? 'active' : ''}`}
                  onClick={() => handleFilterChange('all')}
                >
                All Time
                </button>
                <button
                  className={`filter-btn ${filterType === 'year' ? 'active' : ''}`}
                  onClick={() => handleFilterChange('year')}
                >
                By Year
                </button>
                <button
                  className={`filter-btn ${filterType === 'month' ? 'active' : ''}`}
                  onClick={() => handleFilterChange('month')}
                >
                By Month
                </button>
              </div>

              {filterType === 'year' && (
                <div className="filter-selector">
                  <label>Select Year:</label>
                  <select
                    value={selectedYear}
                    onChange={(e) => handleYearChange(parseInt(e.target.value))}
                    className="filter-select"
                  >
                    {years.map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
              )}

              {filterType === 'month' && (
                <div className="filter-selector">
                  <label>Select Year:</label>
                  <select
                    value={selectedYear}
                    onChange={(e) => handleYearChange(parseInt(e.target.value))}
                    className="filter-select"
                  >
                    {years.map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>

                  <label>Select Month:</label>
                  <select
                    value={selectedMonth}
                    onChange={(e) => handleMonthChange(parseInt(e.target.value))}
                    className="filter-select"
                  >
                    {months.map(month => (
                      <option key={month.value} value={month.value}>{month.name}</option>
                    ))}
                  </select>
                </div>
              )}

              <button className="reset-filter-btn" onClick={resetFilters}>
              Reset Filters
              </button>
            </div>
          )}

          <div className="filter-summary">
            <span className="filter-label">Showing:</span>
            <span className="filter-value">{getFilterSummary()}</span>
            <span className="transaction-count">({filteredTransactions.length} transactions)</span>
          </div>
        </div>

        {/* Earnings Overview */}
        <div className="earnings-overview">
          <div>
            <h3>{filterType === 'month' ? months[selectedMonth].name : months[new Date().getMonth()].name}</h3>
            <p>{formatCurrency(filteredEarnings.thisMonth)}</p>
          </div>
          <div>
            <h3>Total Earnings</h3>
            <p>{formatCurrency(filteredEarnings.total)}</p>
          </div>
        </div>

        {/* Transaction History */}
        <div className="transaction-history">
          <h3>Transaction History</h3>
          {filteredTransactions.length > 0 ? (
            <>
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Amount (₹)</th>
                    <th>Type</th>
                    <th>Course</th>
                    <th>Student</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {currentTransactions.map((txn) => (
                    <tr key={txn.id}>
                      <td>{formatDate(txn.date)}</td>
                      <td>{formatCurrency(txn.amount)}</td>
                      <td>{txn.type}</td>
                      <td>{txn.courseTitle}</td>
                      <td>{txn.studentName}</td>
                      <td className={`status-${txn.status.toLowerCase()}`}>{txn.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="pagination-controls">
                  <div className="pagination-info">
                  Showing {indexOfFirstTransaction + 1} to {Math.min(indexOfLastTransaction, filteredTransactions.length)} of {filteredTransactions.length} transactions
                  </div>

                  <div className="pagination-buttons">
                    <button
                      className="pagination-btn prev-btn"
                      onClick={goToPreviousPage}
                      disabled={currentPage === 1}
                    >
                    ← Previous
                    </button>

                    <div className="page-numbers">
                      {getPageNumbers().map((number, index) => (
                        <button
                          key={index}
                          className={`page-number ${number === currentPage ? 'active' : ''} ${number === '...' ? 'ellipsis' : ''}`}
                          onClick={() => number !== '...' && paginate(number)}
                          disabled={number === '...'}
                        >
                          {number}
                        </button>
                      ))}
                    </div>

                    <button
                      className="pagination-btn next-btn"
                      onClick={goToNextPage}
                      disabled={currentPage === totalPages}
                    >
                    Next →
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="no-transactions">
              <div className="no-transactions-icon">📊</div>
              <p>No transactions found for the selected period.</p>
              <p>Start creating courses to see your earnings!</p>
            </div>
          )}
        </div>
        {/* Payout Method Button */}
        <div className="payout-method-section">
          <button
            className="payout-method-btn"
            onClick={() => setShowPayoutModal(true)}
          >
            <span className="btn-icon">💳</span>
            <span className="btn-text"> Add Payout Method</span>
            <span className="btn-subtext">Current: {payoutMethod}</span>
          </button>
        </div>
        {/* Payout Method Modal */}
        {showPayoutModal && (
          <div className="modal-overlay" onClick={handleCloseModal}>
            <div className="modal-content-s" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3 className="modal-title">Payout Method</h3>
                <button className="modal-close-btn-po" onClick={handleCloseModal}>
                ×
                </button>
              </div>

              <div className="modal-body">
                <div className="current-method">
                  <p><strong>Current Method:</strong> {payoutMethod}</p>
                </div>

                <div className="bank-form">
                  <h4>Bank Details</h4>

                  <div className="bank-form-input-group">
                    <label htmlFor="accountHolder">Account Holder Name</label>
                    <input
                      id="accountHolder"
                      name="accountHolder"
                      placeholder="Enter account holder name"
                      value={bankDetails.accountHolder}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="bank-form-input-group">
                    <label htmlFor="accountNumber">Account Number</label>
                    <input
                      id="accountNumber"
                      name="accountNumber"
                      placeholder="Enter account number"
                      value={bankDetails.accountNumber}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="bank-form-input-group">
                    <label htmlFor="ifsc">IFSC Code</label>
                    <input
                      id="ifsc"
                      name="ifsc"
                      placeholder="Enter IFSC code"
                      value={bankDetails.ifsc}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="bank-form-input-group">
                    <label htmlFor="bankName">Bank Name</label>
                    <input
                      id="bankName"
                      name="bankName"
                      placeholder="Enter bank name"
                      value={bankDetails.bankName}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>

              <div className="modal-footer">

                <button className="insta_save-btn" onClick={handleSave}>
                Save Changes
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InstructorPayout;
